import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { MapContainer, Rectangle, Tooltip, useMap, GeoJSON } from 'react-leaflet';
import { apiClient } from '../../api/client';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { EsriVectorBasemap } from '@/lib/basemap';

// ── Types ────────────────────────────────────────────────────────────────

interface GridCell {
  lat: number;
  lon: number;
  sat_ft: number;
  well_count: number;
  aquifer: string;
}

interface GridResponse {
  grid: GridCell[];
  bounds: { north: number; south: number; east: number; west: number };
  resolution: number;
  total_wells: number;
}

interface CountyStat {
  county: string;
  avg_sat: number;
  well_count: number;
  min_sat: number;
  max_sat: number;
}

interface AquiferStat {
  aquifer: string;
  avg_sat: number;
  well_count: number;
  area_coverage: number;
}

interface StatsResponse {
  by_county: CountyStat[];
  by_aquifer: AquiferStat[];
}

// ── Color scale ──────────────────────────────────────────────────────────

const COLOR_SCALE = [
  { min: 300, max: Infinity, color: '#1e3a8a', label: '> 300 ft' },
  { min: 200, max: 300, color: '#0891b2', label: '200–300 ft' },
  { min: 100, max: 200, color: '#059669', label: '100–200 ft' },
  { min: 50, max: 100, color: '#ca8a04', label: '50–100 ft' },
  { min: 20, max: 50, color: '#ea580c', label: '20–50 ft' },
  { min: 0, max: 20, color: '#dc2626', label: '< 20 ft' },
];

function getSatColor(sat: number): string {
  for (const band of COLOR_SCALE) {
    if (sat >= band.min) return band.color;
  }
  return '#dc2626';
}

function getOpacity(wellCount: number): number {
  // More wells = more confidence = more opaque
  const clamped = Math.min(Math.max(wellCount, 1), 20);
  return 0.3 + (clamped / 20) * 0.4; // range 0.3 to 0.7
}

// ── Grid Cell Component (memoized) ──────────────────────────────────────

interface GridCellProps {
  cell: GridCell;
  halfRes: number;
}

const GridCellRect = React.memo<GridCellProps>(({ cell, halfRes }) => {
  const bounds: [[number, number], [number, number]] = [
    [cell.lat - halfRes, cell.lon - halfRes],
    [cell.lat + halfRes, cell.lon + halfRes],
  ];

  return (
    <Rectangle
      bounds={bounds}
      pathOptions={{
        color: getSatColor(cell.sat_ft),
        fillColor: getSatColor(cell.sat_ft),
        fillOpacity: getOpacity(cell.well_count),
        weight: 0,
      }}
    >
      <Tooltip sticky>
        <div style={{ fontSize: 13, lineHeight: 1.5 }}>
          <strong>Sat. Thickness:</strong> {cell.sat_ft.toFixed(1)} ft<br />
          <strong>Well Count:</strong> {cell.well_count}<br />
          <strong>Aquifer:</strong> {cell.aquifer}
        </div>
      </Tooltip>
    </Rectangle>
  );
});
GridCellRect.displayName = 'GridCellRect';

// ── Map invalidation on container mount ──────────────────────────────────

function MapResizer() {
  const map = useMap();
  useEffect(() => {
    setTimeout(() => map.invalidateSize(), 100);
  }, [map]);
  return null;
}

// ── Texas Boundary URLs (US Census Bureau cartographic boundaries) ────────
// 20m resolution — lightweight, good for state-level view
const TX_STATE_URL = 'https://raw.githubusercontent.com/PublicaMundi/MappingAPI/master/data/geojson/us-states.json';
const TX_COUNTIES_URL = 'https://raw.githubusercontent.com/plotly/datasets/master/geojson-counties-fips.json';
const TX_FIPS = '48'; // Texas FIPS code

// ── Boundary styles ─────────────────────────────────────────────────────

const stateStyle: L.PathOptions = {
  color: '#06b6d4',
  weight: 2,
  fillColor: 'transparent',
  fillOpacity: 0,
  interactive: false,
};

const countyStyle: L.PathOptions = {
  color: '#334155',
  weight: 0.5,
  fillColor: 'transparent',
  fillOpacity: 0,
  interactive: false,
};

// ── Main Component ───────────────────────────────────────────────────────

export default function BlueDiamond() {
  const [gridData, setGridData] = useState<GridResponse | null>(null);
  const [stats, setStats] = useState<StatsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Boundaries
  const [texasOutline, setTexasOutline] = useState<any>(null);
  const [texasCounties, setTexasCounties] = useState<any>(null);
  const [showCounties, setShowCounties] = useState(true);

  // Filters
  const [aquiferFilter, setAquiferFilter] = useState<string>('');
  const [minSatFilter, setMinSatFilter] = useState<number>(0);

  // Aquifer layer (selects which precomputed grid to load)
  const [aquiferLayer, setAquiferLayer] = useState<string>('all');
  const [availableAquiferLayers, setAvailableAquiferLayers] = useState<string[]>(['all']);

  // Time horizon
  const [horizon, setHorizon] = useState<string>('current');
  const [availableHorizons, setAvailableHorizons] = useState<string[]>(['current']);

  // Fetch available horizons + aquifer layers on mount
  useEffect(() => {
    const HORIZON_ORDER = ['current', '3mo', '1yr', '2yr', '5yr', '10yr', '20yr'];
    apiClient.get('/phreatic/blue-diamond/horizons')
      .then(res => {
        const raw: string[] = res.data.horizons || ['current'];
        const sorted = raw.sort((a, b) => {
          const ia = HORIZON_ORDER.indexOf(a);
          const ib = HORIZON_ORDER.indexOf(b);
          return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
        });
        setAvailableHorizons(sorted);
        if (res.data.aquifer_layers) {
          setAvailableAquiferLayers(res.data.aquifer_layers);
        }
      })
      .catch(() => {});
  }, []);

  // Fetch grid data (re-fetch when horizon changes)
  useEffect(() => {
    setLoading(true);
    setError(null);
    apiClient
      .get<GridResponse>(`/phreatic/blue-diamond/grid?horizon=${horizon}&aquifer=${encodeURIComponent(aquiferLayer)}`)
      .then((res) => {
        setGridData(res.data);
      })
      .catch((err) => {
        setError(err?.response?.data?.detail || 'Failed to load grid data');
      })
      .finally(() => setLoading(false));
  }, [horizon, aquiferLayer]);

  // Fetch stats
  useEffect(() => {
    apiClient
      .get<StatsResponse>('/phreatic/blue-diamond/stats')
      .then((res) => setStats(res.data))
      .catch(() => {});
  }, []);

  // Fetch Texas state outline
  useEffect(() => {
    fetch(TX_STATE_URL)
      .then(r => r.json())
      .then(data => {
        // Filter to just Texas
        const tx = {
          type: 'FeatureCollection' as const,
          features: data.features.filter((f: any) => f.properties?.name === 'Texas'),
        };
        if (tx.features.length > 0) setTexasOutline(tx);
      })
      .catch(() => {});
  }, []);

  // Fetch Texas county boundaries
  useEffect(() => {
    fetch(TX_COUNTIES_URL)
      .then(r => r.json())
      .then(data => {
        // Filter to Texas FIPS (48xxx)
        const txCounties = {
          type: 'FeatureCollection' as const,
          features: data.features.filter((f: any) =>
            f.properties?.STATE === TX_FIPS || f.id?.startsWith(TX_FIPS)
          ),
        };
        if (txCounties.features.length > 0) setTexasCounties(txCounties);
      })
      .catch(() => {});
  }, []);

  // Unique aquifers for dropdown
  const aquifers = useMemo(() => {
    if (!gridData) return [];
    const set = new Set(gridData.grid.map((c) => c.aquifer));
    return Array.from(set).sort();
  }, [gridData]);

  // Filtered grid cells
  const filteredCells = useMemo(() => {
    if (!gridData) return [];
    return gridData.grid.filter((cell) => {
      if (aquiferFilter && cell.aquifer !== aquiferFilter) return false;
      if (cell.sat_ft < minSatFilter) return false;
      return true;
    });
  }, [gridData, aquiferFilter, minSatFilter]);

  const halfRes = gridData ? gridData.resolution / 2 : 0.025;

  // Top 10 counties
  const topCounties = useMemo(() => {
    if (!stats) return [];
    return stats.by_county.slice(0, 10);
  }, [stats]);

  if (loading) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          background: '#0f172a',
          color: '#94a3b8',
          fontSize: 18,
          flexDirection: 'column',
          gap: 16,
        }}
      >
        <div
          style={{
            width: 48,
            height: 48,
            border: '4px solid #1e3a8a',
            borderTop: '4px solid #38bdf8',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
          }}
        />
        <div>Computing Texas groundwater grid...</div>
        <div style={{ fontSize: 13, color: '#64748b' }}>
          First load may take 30-60 seconds while the IDW grid is built
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100vh',
          background: '#0f172a',
          color: '#ef4444',
          fontSize: 16,
        }}
      >
        Error: {error}
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', height: '100vh', width: '100%', background: '#0f172a' }}>
      {/* Title bar */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1000,
          background: 'linear-gradient(180deg, rgba(15,23,42,0.95) 0%, rgba(15,23,42,0.7) 100%)',
          padding: '12px 24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid #1e293b',
        }}
      >
        <div>
          <h1
            style={{
              margin: 0,
              fontSize: 20,
              fontWeight: 700,
              color: '#e2e8f0',
              letterSpacing: '-0.02em',
            }}
          >
            Blue Diamond{' '}
            <span style={{ color: '#64748b', fontWeight: 400, fontSize: 14 }}>
              — Texas Groundwater Discovery
            </span>
          </h1>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, color: '#94a3b8' }}>
          <span>{gridData?.total_wells?.toLocaleString()} wells</span>
          <span style={{ color: '#334155' }}>|</span>
          <span>{filteredCells.length.toLocaleString()} grid cells</span>
          {aquiferLayer !== 'all' && (
            <>
              <span style={{ color: '#334155' }}>|</span>
              <span style={{ color: '#f59e0b' }}>{aquiferLayer}</span>
            </>
          )}
          {horizon !== 'current' && (
            <>
              <span style={{ color: '#334155' }}>|</span>
              <span style={{ color: '#06b6d4' }}>V5 Predicted: {horizon}</span>
            </>
          )}
        </div>
      </div>

      {/* Filter controls */}
      <div
        style={{
          position: 'absolute',
          top: 56,
          left: 16,
          zIndex: 1000,
          background: 'rgba(15,23,42,0.92)',
          borderRadius: 8,
          padding: '12px 16px',
          border: '1px solid #1e293b',
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          minWidth: 220,
        }}
      >
        <div style={{ fontSize: 12, fontWeight: 600, color: '#94a3b8', textTransform: 'uppercase' }}>
          Filters
        </div>

        {availableHorizons.length > 1 && (
          <div>
            <label style={{ fontSize: 11, color: '#64748b', display: 'block', marginBottom: 4 }}>
              Time Horizon
            </label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {availableHorizons.map(hz => (
                <button
                  key={hz}
                  onClick={() => setHorizon(hz)}
                  style={{
                    padding: '3px 8px',
                    fontSize: 10,
                    borderRadius: 4,
                    border: '1px solid',
                    borderColor: horizon === hz ? '#06b6d4' : '#334155',
                    background: horizon === hz ? 'rgba(6,182,212,0.2)' : 'transparent',
                    color: horizon === hz ? '#06b6d4' : '#94a3b8',
                    cursor: 'pointer',
                  }}
                >
                  {hz === 'current' ? 'Now' : hz}
                </button>
              ))}
            </div>
          </div>
        )}

        {availableAquiferLayers.length > 1 && (
          <div>
            <label style={{ fontSize: 11, color: '#64748b', display: 'block', marginBottom: 4 }}>
              Aquifer Layer
            </label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {availableAquiferLayers.map(aq => (
                <button
                  key={aq}
                  onClick={() => setAquiferLayer(aq)}
                  style={{
                    padding: '3px 8px',
                    fontSize: 10,
                    borderRadius: 4,
                    border: '1px solid',
                    borderColor: aquiferLayer === aq ? '#f59e0b' : '#334155',
                    background: aquiferLayer === aq ? 'rgba(245,158,11,0.2)' : 'transparent',
                    color: aquiferLayer === aq ? '#f59e0b' : '#94a3b8',
                    cursor: 'pointer',
                  }}
                >
                  {aq === 'all' ? 'All (Blended)' : aq}
                </button>
              ))}
            </div>
          </div>
        )}

        <div>
          <label style={{ fontSize: 11, color: '#64748b', display: 'block', marginBottom: 4 }}>
            Cell Filter
          </label>
          <select
            value={aquiferFilter}
            onChange={(e) => setAquiferFilter(e.target.value)}
            style={{
              width: '100%',
              background: '#1e293b',
              color: '#e2e8f0',
              border: '1px solid #334155',
              borderRadius: 4,
              padding: '4px 8px',
              fontSize: 12,
            }}
          >
            <option value="">All Aquifers</option>
            {aquifers.map((aq) => (
              <option key={aq} value={aq}>
                {aq}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label style={{ fontSize: 11, color: '#64748b', display: 'block', marginBottom: 4 }}>
            Min Sat. Thickness: {minSatFilter} ft
          </label>
          <input
            type="range"
            min={0}
            max={500}
            step={10}
            value={minSatFilter}
            onChange={(e) => setMinSatFilter(Number(e.target.value))}
            style={{ width: '100%', accentColor: '#0891b2' }}
          />
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#94a3b8', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={showCounties}
            onChange={(e) => setShowCounties(e.target.checked)}
            style={{ accentColor: '#0891b2' }}
          />
          County lines
        </label>
      </div>

      {/* Legend */}
      <div
        style={{
          position: 'absolute',
          bottom: 32,
          left: 16,
          zIndex: 1000,
          background: 'rgba(15,23,42,0.92)',
          borderRadius: 8,
          padding: '12px 16px',
          border: '1px solid #1e293b',
        }}
      >
        <div
          style={{
            fontSize: 12,
            fontWeight: 600,
            color: '#94a3b8',
            textTransform: 'uppercase',
            marginBottom: 8,
          }}
        >
          Saturated Thickness
        </div>
        {COLOR_SCALE.map((band) => (
          <div
            key={band.label}
            style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}
          >
            <div
              style={{
                width: 16,
                height: 16,
                borderRadius: 2,
                background: band.color,
                opacity: 0.7,
              }}
            />
            <span style={{ fontSize: 12, color: '#cbd5e1' }}>{band.label}</span>
          </div>
        ))}
      </div>

      {/* Stats panel - top 10 counties */}
      {topCounties.length > 0 && (
        <div
          style={{
            position: 'absolute',
            top: 56,
            right: 16,
            zIndex: 1000,
            background: 'rgba(15,23,42,0.92)',
            borderRadius: 8,
            padding: '12px 16px',
            border: '1px solid #1e293b',
            maxHeight: 'calc(100vh - 100px)',
            overflowY: 'auto',
            minWidth: 240,
          }}
        >
          <div
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: '#94a3b8',
              textTransform: 'uppercase',
              marginBottom: 8,
            }}
          >
            Top Counties by Avg Sat. Thickness
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr style={{ color: '#64748b', textAlign: 'left' }}>
                <th style={{ padding: '2px 4px' }}>County</th>
                <th style={{ padding: '2px 4px', textAlign: 'right' }}>Avg ft</th>
                <th style={{ padding: '2px 4px', textAlign: 'right' }}>Wells</th>
              </tr>
            </thead>
            <tbody>
              {topCounties.map((c, i) => (
                <tr key={c.county} style={{ color: '#cbd5e1' }}>
                  <td style={{ padding: '2px 4px' }}>
                    <span style={{ color: '#475569', marginRight: 4 }}>{i + 1}.</span>
                    {c.county}
                  </td>
                  <td style={{ padding: '2px 4px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {c.avg_sat.toFixed(0)}
                  </td>
                  <td style={{ padding: '2px 4px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {c.well_count}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Map */}
      <MapContainer
        center={[31.0, -99.5]}
        zoom={6}
        style={{ height: '100%', width: '100%' }}
        zoomControl={true}
      >
        <MapResizer />
        <EsriVectorBasemap />
        {/* County boundaries (below grid, non-interactive so grid tooltips work) */}
        {showCounties && texasCounties && (
          <GeoJSON key="tx-counties" data={texasCounties} style={() => countyStyle} />
        )}
        {/* Grid cells */}
        {filteredCells.map((cell) => (
          <GridCellRect
            key={`${cell.lat}-${cell.lon}`}
            cell={cell}
            halfRes={halfRes}
          />
        ))}
        {/* Texas state outline (on top of grid) */}
        {texasOutline && (
          <GeoJSON key="tx-outline" data={texasOutline} style={() => stateStyle} />
        )}
      </MapContainer>
    </div>
  );
}
